import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Edit2, Plus, Trash2, X } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import assetNoPolls from '@/assets/noPolls.svg'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import type { PollData, PollOption, StepProps } from "./types"

const emptyPoll = (): Omit<PollData, "id"> => ({
  question: "",
  questionType: "single",
  options: [
    { id: `opt-${Date.now()}-1`, text: "" },
    { id: `opt-${Date.now()}-2`, text: "" },
  ],
})

function validatePollForm(form: Omit<PollData, "id">, t: (key: string) => string) {
  const errs: Record<string, string> = {}
  if (!form.question.trim()) {
    errs.question = t('webinar.validation.pollQuestionRequired')
  } else if (form.question.trim().length < 5) {
    errs.question = t('webinar.validation.pollQuestionMin5')
  } else if (form.question.trim().length > 200) {
    errs.question = t('webinar.validation.pollQuestionMax200')
  }

  const filled = form.options.filter((o) => o.text.trim())
  if (filled.length < 2) {
    errs.options = t('webinar.validation.pollOptionsMin2')
  } else {
    const unique = new Set(filled.map((o) => o.text.trim().toLowerCase()))
    if (unique.size < filled.length) errs.options = t('webinar.validation.pollOptionsUnique')
  }

  const optionErrs: Record<string, string> = {}
  form.options.forEach((o) => {
    if (o.text.length > 100) optionErrs[o.id] = t('webinar.validation.pollOptionMax100')
  })
  if (Object.keys(optionErrs).length) errs._optionErrs = JSON.stringify(optionErrs)

  return errs
}

export function StepPolling({ data, onChange }: StepProps) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<Omit<PollData, "id">>(emptyPoll())
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const openAdd = () => { setForm(emptyPoll()); setFormErrors({}); setEditId(null); setOpen(true) }
  const openEdit = (poll: PollData) => {
    setForm({ question: poll.question, questionType: poll.questionType, options: poll.options })
    setFormErrors({})
    setEditId(poll.id)
    setOpen(true)
  }

  const handleSave = () => {
    const errs = validatePollForm(form, t)
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return }
    const validOptions = form.options.filter((o) => o.text.trim())
    if (editId) {
      onChange({ polls: data.polls.map((p) => p.id === editId ? { ...form, id: editId, options: validOptions } : p) })
    } else {
      onChange({ polls: [...data.polls, { ...form, id: `poll-${Date.now()}`, options: validOptions }] })
    }
    setOpen(false)
  }

  const handleDelete = (id: string) => {
    onChange({ polls: data.polls.filter((p) => p.id !== id) })
    setDeleteId(null)
  }

  const addOption = () => {
    setForm((p) => ({ ...p, options: [...p.options, { id: `opt-${Date.now()}`, text: "" }] }))
    setFormErrors((prev) => { const next = { ...prev }; delete next.options; return next })
  }

  const updateOption = (id: string, text: string) => {
    setForm((p) => ({ ...p, options: p.options.map((o) => o.id === id ? { ...o, text } : o) }))
    setFormErrors((prev) => { const next = { ...prev }; delete next.options; delete next._optionErrs; return next })
  }

  const removeOption = (id: string) => {
    setForm((p) => ({ ...p, options: p.options.filter((o) => o.id !== id) }))
    setFormErrors((prev) => { const next = { ...prev }; delete next.options; return next })
  }

  const optionErrs: Record<string, string> = formErrors._optionErrs
    ? JSON.parse(formErrors._optionErrs)
    : {}

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.polls")}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.createLivePollsDesc")}</p>
        </div>
        {data.polls.length > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1" /> {t("webinar.createPoll")}
          </Button>
        )}
      </div>

      {/* Empty state */}
      {data.polls.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-stroke-100 dark:border-border dark:border-border flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="h-24 w-24 rounded-full flex items-center justify-center">
            <img src={assetNoPolls} alt=""/>
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white">{t("webinar.noPollsYet")}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t("webinar.createPollsToEngage")}</p>
          </div>
          <Button type="button" onClick={openAdd} className="mt-1">
            <Plus className="w-4 h-4 mr-2" /> Create Poll
          </Button>
        </div>
      )}

      {/* Polls table */}
      {data.polls.length > 0 && (
        <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border overflow-hidden">
          <div className="grid grid-cols-12 bg-slate-50 dark:bg-card/40 px-4 py-2.5 border-b border-stroke-100 dark:border-border dark:border-border">
            <span className="col-span-7 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{t("webinar.question")}</span>
            <span className="col-span-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{t("webinar.type")}</span>
            <span className="col-span-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-center">{t("webinar.actions")}</span>
          </div>
          {data.polls.map((poll, idx) => (
            <div key={poll.id}
              className={`grid grid-cols-12 px-4 py-3 items-center ${idx !== data.polls.length - 1 ? "border-b border-stroke-100 dark:border-border dark:border-slate-800" : ""}`}>
              <span className="col-span-7 text-sm text-slate-800 dark:text-white truncate pr-2">{poll.question}</span>
              <span className="col-span-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${poll.questionType === "single" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"}`}>
                  {poll.questionType === "single" ? t("webinar.singleChoice") : t("webinar.multiChoice")}
                </span>
              </span>
              <div className="col-span-2 flex justify-center gap-1">
                <button type="button" onClick={() => openEdit(poll)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setDeleteId(poll.id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 dark:text-slate-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Poll Dialog */}
      <Dialog open={open} onOpenChange={setOpen} size="sm">
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? t("webinar.editPoll") : t("webinar.createPoll")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-medium text-sm">{t("webinar.question")} <span className="text-red-500">*</span></Label>
                <span className={`text-xs ${form.question.length > 200 ? "text-red-500" : "text-slate-400"}`}>
                  {form.question.length}/200
                </span>
              </div>
              <Input
                value={form.question}
                onChange={(e) => {
                  setForm((p) => ({ ...p, question: e.target.value }))
                  setFormErrors((prev) => { const next = { ...prev }; delete next.question; return next })
                }}
                placeholder={t("webinar.enterPollQuestion")}
                className={`h-11 ${formErrors.question ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              />
              {formErrors.question && <p className="text-xs text-red-500 mt-1">{formErrors.question}</p>}
            </div>

            <div>
              <Label className="font-medium mb-2 block text-sm">{t("webinar.answerType")}</Label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 rounded-xl border-2 p-3 cursor-pointer transition-colors ${form.questionType === "single" ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border"}`}>
                  <input type="radio" checked={form.questionType === "single"}
                    onChange={() => setForm((p) => ({ ...p, questionType: "single" }))} className="accent-primary" />
                  <span className="text-sm font-medium text-slate-800 dark:text-white">{t("webinar.singleChoice")}</span>
                </label>
                <label className={`flex-1 flex items-center gap-2 rounded-xl border-2 p-3 cursor-pointer transition-colors ${form.questionType === "multiple" ? "border-primary bg-primary/5" : "border-stroke-100 dark:border-border"}`}>
                  <input type="radio" checked={form.questionType === "multiple"}
                    onChange={() => setForm((p) => ({ ...p, questionType: "multiple" }))} className="accent-primary" />
                  <span className="text-sm font-medium text-slate-800 dark:text-white">{t("webinar.multipleChoice")}</span>
                </label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Label className="font-medium text-sm">{t("webinar.options")}</Label>
                  <span className="text-slate-400 text-xs font-normal">(min 2, max 8)</span>
                </div>
                {form.options.length < 8 && (
                  <Button type="button" variant="ghost" size="sm" onClick={addOption} className="h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> {t("webinar.addOption")}
                  </Button>
                )}
              </div>
              {formErrors.options && (
                <p className="text-xs text-red-500 mb-2">{formErrors.options}</p>
              )}
              <div className="space-y-2">
                {form.options.map((opt: PollOption, idx: number) => (
                  <div key={opt.id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-5 shrink-0 text-center">{idx + 1}.</span>
                      <Input value={opt.text} onChange={(e) => updateOption(opt.id, e.target.value)}
                        placeholder={`Option ${idx + 1}`}
                        className={`h-9 flex-1 ${optionErrs[opt.id] ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                      {form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(opt.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {optionErrs[opt.id] && (
                      <p className="text-xs text-red-500 pl-7">{optionErrs[opt.id]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("webinar.cancel")}</Button>
            <Button type="button" onClick={handleSave}>
              {editId ? t("webinar.saveChanges") : t("webinar.createPoll")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)} size="xs">
        <DialogContent>
          <DialogHeader><DialogTitle>{t("webinar.deletePoll")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300 py-1">{t("webinar.confirmDeletePoll")}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)}>{t("webinar.cancel")}</Button>
            <Button type="button" variant="destructive" onClick={() => deleteId && handleDelete(deleteId)}>
              <Trash2 className="w-4 h-4 mr-2" /> {t("webinar.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
